const isMenuOpen = () => {
	const classList = document.querySelector('.ddc-document')?.classList;
	return classList?.contains('active');
};

/**
 * Waits for an element to appear in the DOM using a MutationObserver.
 *
 * @param {string} selector - The CSS selector of the target element.
 * @param {number} timeout - Timeout in milliseconds (default: 15000).
 * @returns {Promise<Element>} A promise that resolves with the found element or rejects on timeout.
 */
export const waitForElement = (selector, timeout = 15000) => {
	// eslint-disable-next-line consistent-return
	return new Promise((resolve, reject) => {
		// Check immediately first
		const element = document.querySelector(selector);
		if (element) {
			return resolve(element);
		}

		let observer;
		const timeoutId = setTimeout(() => {
			if (observer) {
				observer.disconnect();
			}
			reject(
				new Error(
					`Element with selector "${selector}" not found within ${timeout}ms`
				)
			);
		}, timeout);

		// Set up the observer
		observer = new MutationObserver((mutations, obs) => {
			const foundElement = document.querySelector(selector);
			if (foundElement) {
				clearTimeout(timeoutId);
				obs.disconnect();
				resolve(foundElement);
			}
		});

		// Start observing
		observer.observe(document.documentElement, {
			childList: true,
			subtree: true
		});
	});
};

/**
 *
 * @param {function} callback
 * @returns
 */
export const onLdClientReady = async callback => {
	if (!window.ldClient) {
		document.addEventListener('LDClientReady', callback);
	} else {
		await window.ldClient.waitUntilReady();
		callback();
	}
};

/**
 *
 * @param {boolean} eventResolved
 * @param {string} eventName
 * @returns
 */
export const waitForEvent = (eventResolved, eventName) => {
	return new Promise(resolve => {
		if (eventResolved !== false && eventResolved !== undefined) {
			resolve(eventResolved);
		} else {
			window.addEventListener(
				eventName,
				data => {
					resolve(data);
				},
				{ once: true }
			);
		}
	});
};

export const waitForMenu = async (timeout = null) => {
	const menuOpenEventName = 'mobile-navbar/open';
	// targetElements targets both modern and legacy menu elements, respectively
	const targetElements = [
		'.ddc-mobile-fragment-nav',
		'[data-location~="primary-menu"]'
	];

	return new Promise(resolve => {
		if (isMenuOpen()) {
			Promise.race(
				targetElements.map(selector => waitForElement(selector, 10000))
			)
				.then(() => resolve(true))
				.catch(() => resolve(false));
		} else {
			// Check if DDC pubsub is available
			if (!window.DDC?.pubsub?.subscribe) {
				resolve(false);
				return;
			}

			let timeoutId;
			
			const handleMenuOpenEvent = async () => {
				if (timeoutId) clearTimeout(timeoutId);
				Promise.race(
					targetElements.map(selector => waitForElement(selector, 10000))
				)
					.then(() => {
						resolve(true);
						window.DDC?.pubsub?.unsubscribe(
							menuOpenEventName,
							handleMenuOpenEvent
						);
					})
					.catch(() => resolve(false));
			};
			
			if (timeout) {
				timeoutId = setTimeout(() => {
					window.DDC?.pubsub?.unsubscribe(
						menuOpenEventName,
						handleMenuOpenEvent
					);
					resolve(false);
				}, timeout);
			}
			
			window.DDC.pubsub.subscribe(menuOpenEventName, handleMenuOpenEvent);
		}
	});
};

export const isDocumentReady = () => {
	return new Promise(resolve => {
		if (document.readyState !== 'loading') {
			resolve();
		} else {
			document.addEventListener('DOMContentLoaded', resolve, { once: true });
		}
	});
};

/**
 *
 * @returns {Promise<any>}
 */
export const isWIAPILoaded = async () => {
	return waitForEvent(
		window.DDC?.PrivateAPI?.integrationsLoaded,
		'WIAPILoaded'
	);
};

export const waitForObject = async (
	objectPath,
	interval = 50,
	timeout = 15000
) => {
	return new Promise((resolve, reject) => {
		let checkObject;

		const timeoutHandler = setTimeout(() => {
			(window?.clearInterval || global?.clearInterval || (() => {}))(
				checkObject
			);
			reject(
				new Error(
					`Timed out after ${timeout}ms waiting for ${objectPath} to become available`
				)
			);
		}, timeout);

		checkObject = setInterval(() => {
			const parts = objectPath.split('.');
			let current = window;
			const objectExists = parts.every(part => {
				if (current[part] === undefined) {
					return false;
				}
				current = current[part];
				return true;
			});
			if (!objectExists) {
				return;
			}
			(window?.clearInterval || global?.clearInterval || (() => {}))(
				checkObject
			);
			(window?.clearTimeout || global?.clearTimeout || (() => {}))(
				timeoutHandler
			);
			resolve(true);
		}, interval);
	});
};
